/**
 * Setup and How-To, one page under Settings › System (piece 15; decision
 * 1842): every stepper in the application, each with its done count where it
 * is the installation's own and a link to where it stands. 🔒 The stepper stays
 * the one source: this list names each one and counts its steps through the
 * stepper's own rule, and no step, count or article is stored for it.
 */
import { ACCOUNTING_SETUP_STEPS, ACCOUNTING_SETUP_STEPS_PATH, accountingSetupSteps, type AccountingSetupStepsView } from '../accounting/accountingSetupSteps';
import { BANK_STEPS, BANK_STEPS_PATH, bankSteps, type BankStepsView } from '../accounting/bankSteps';
import { BACKUP_STEPS, BACKUP_STEPS_PATH, backupSteps, type BackupStepsView } from '../files/backup';
import { LOGIN_STEPS, LOGIN_STEPS_PATH, RECOVERY_COPY_ROUTE, TURN_ON_LOGINS_ROUTE, loginSteps, type LoginStepsView } from '../identity/loginSteps';
import { ZOHO_IMPORT_STEPS, zohoImportSteps } from '../import/importSteps';
import { IMPORT_SCREEN_ROUTE, ZOHO_BOOKS_IMPORT_PATH, type ZohoBooksPresetView } from '../import/importRun';
import { HIRE_WINDOW_ROUTE } from '../hr/hire';
import { FIRST_HIRE_STEPS, FIRST_HIRE_STEPS_PATH, firstHireSteps, type FirstHireStepsView } from '../hr/firstHireSteps';
import { MAIL_SETTINGS_ROUTE } from '../mail/client';
import { MAIL_SETUP_STEPS, MAIL_SETUP_STEPS_PATH, mailSetupSteps, type MailSetupStepsView } from '../mail/mailSetupSteps';
import { EXEMPT_CUSTOMER_STEPS } from '../tax/exemptSteps';
import { RETURN_STEPS } from '../tax/returnSteps';
import { STATE_SETUP_STEPS } from '../tax/stateSetup';
import { RECORD_ROUTES } from '../pages/recordPages';
import type { StringKey } from '../strings';

export const SETUP_GUIDES_ROUTE = '/settings/system/setup-and-how-to';

/** The pages a stepper stands on, where the shared package names none of its own. */
export const STORAGE_AND_BACKUPS_ROUTE = RECOVERY_COPY_ROUTE;
export const BANKING_PAGE_ROUTE = '/banking';
export const ACCOUNTING_BASIS_ROUTE = '/settings/accounting/basis';
export const SALES_TAX_PAGE_ROUTE = '/settings/tax/sales-tax';

/** An installation-wide stepper is done once; a per-record one runs again for each state, site or return. */
export type SetupGuideScope = 'installation' | 'perRecord';

export const SETUP_GUIDES = ['import', 'backups', 'accounting', 'bank', 'mail', 'logins', 'firstHire', 'state', 'exemptCustomer', 'fileReturn'] as const;
export type SetupGuide = (typeof SETUP_GUIDES)[number];

export interface SetupGuideEntry {
  /** The stepper's name as this list draws it, in Title Case. */
  title: StringKey;
  /** One sentence of what it takes you through. */
  what: StringKey;
  /** Where the stepper stands; a per-record stepper's list of records. */
  route: string;
  scope: SetupGuideScope;
  /** How many steps it has. */
  steps: number;
  /** The read its facts come from, an installation-wide stepper's alone. */
  factsPath: string | null;
}

/** 🔒 Every stepper, in the order a new installation meets them (decision 1842). */
export const SETUP_GUIDE_ENTRIES: Readonly<Record<SetupGuide, SetupGuideEntry>> = {
  import: { title: 'setupGuides.import.title', what: 'setupGuides.import.what', route: IMPORT_SCREEN_ROUTE, scope: 'installation', steps: ZOHO_IMPORT_STEPS.length, factsPath: ZOHO_BOOKS_IMPORT_PATH },
  backups: { title: 'setupGuides.backups.title', what: 'setupGuides.backups.what', route: STORAGE_AND_BACKUPS_ROUTE, scope: 'installation', steps: BACKUP_STEPS.length, factsPath: BACKUP_STEPS_PATH },
  accounting: { title: 'setupGuides.accounting.title', what: 'setupGuides.accounting.what', route: ACCOUNTING_BASIS_ROUTE, scope: 'installation', steps: ACCOUNTING_SETUP_STEPS.length, factsPath: ACCOUNTING_SETUP_STEPS_PATH },
  bank: { title: 'setupGuides.bank.title', what: 'setupGuides.bank.what', route: BANKING_PAGE_ROUTE, scope: 'installation', steps: BANK_STEPS.length, factsPath: BANK_STEPS_PATH },
  mail: { title: 'setupGuides.mail.title', what: 'setupGuides.mail.what', route: MAIL_SETTINGS_ROUTE, scope: 'installation', steps: MAIL_SETUP_STEPS.length, factsPath: MAIL_SETUP_STEPS_PATH },
  logins: { title: 'setupGuides.logins.title', what: 'setupGuides.logins.what', route: TURN_ON_LOGINS_ROUTE, scope: 'installation', steps: LOGIN_STEPS.length, factsPath: LOGIN_STEPS_PATH },
  // 🔒 Decision 6842 (T3003): the First hire guide, drawn above the Hire window while a step stands undone.
  firstHire: { title: 'setupGuides.firstHire.title', what: 'setupGuides.firstHire.what', route: HIRE_WINDOW_ROUTE, scope: 'installation', steps: FIRST_HIRE_STEPS.length, factsPath: FIRST_HIRE_STEPS_PATH },
  state: { title: 'setupGuides.state.title', what: 'setupGuides.state.what', route: SALES_TAX_PAGE_ROUTE, scope: 'perRecord', steps: STATE_SETUP_STEPS.length, factsPath: null },
  exemptCustomer: { title: 'setupGuides.exemptCustomer.title', what: 'setupGuides.exemptCustomer.what', route: RECORD_ROUTES.SiteRecord.list, scope: 'perRecord', steps: EXEMPT_CUSTOMER_STEPS.length, factsPath: null },
  fileReturn: { title: 'setupGuides.fileReturn.title', what: 'setupGuides.fileReturn.what', route: SALES_TAX_PAGE_ROUTE, scope: 'perRecord', steps: RETURN_STEPS.length, factsPath: null },
};

/**
 * 🔒 How many steps a guide shows now (T3563 review): every guide counts its declared steps, since the Import guide's one
 * step drawn only once a range was written retired under decision 10763. The answer is kept for the page that reads it.
 */
export function setupGuideSteps(guide: SetupGuide, _facts: unknown): number {
  return SETUP_GUIDE_ENTRIES[guide].steps;
}

/**
 * 🔒 How many of an installation-wide stepper's steps are done, through that
 * stepper's own rule and never a second copy of it (decision 1842). A
 * per-record stepper, or an answer that is missing, counts nothing.
 */
export function setupGuideDone(guide: SetupGuide, facts: unknown, now: Date): number | null {
  if (facts === null || facts === undefined || typeof facts !== 'object') return null;
  const count = (states: Record<string, { done: boolean }>) => Object.values(states).filter((one) => one.done).length;
  switch (guide) {
    case 'import': return count(zohoImportSteps(facts as ZohoBooksPresetView));
    case 'backups': return count(backupSteps(facts as BackupStepsView, now));
    case 'accounting': return count(accountingSetupSteps(facts as AccountingSetupStepsView));
    case 'bank': return count(bankSteps(facts as BankStepsView));
    case 'mail': return count(mailSetupSteps(facts as MailSetupStepsView));
    case 'logins': return count(loginSteps(facts as LoginStepsView));
    case 'firstHire': return count(firstHireSteps(facts as FirstHireStepsView));
    default: return null;
  }
}
